import React from 'react';
import { HELP_TEXT } from 'constants/helpText';

export default function HelpOverlay({ isOpen, onClose }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999]">
      <button
        type="button"
        aria-label="Close help"
        className="absolute inset-0 bg-black/40 w-full h-full"
        onClick={onClose}
      />

      <div
        className="
          fixed bottom-0 left-0 right-0
          max-h-[85vh] overflow-y-auto
          rounded-t-2xl bg-white p-5 shadow-[0_-8px_30px_rgba(0,0,0,0.25)]

          md:top-1/2 md:left-1/2 md:right-auto md:bottom-auto
          md:w-[90%] md:max-w-lg
          md:-translate-x-1/2 md:-translate-y-1/2
          md:rounded-2xl md:shadow-[0_10px_40px_rgba(0,0,0,0.25)]
        "
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-extrabold text-stone-800">
              How to create your adoption post
            </h2>
            <p className="mt-1 text-sm text-stone-600">
              A quick guide to help you create a clear, eye-catching image for
              your animal listing.
            </p>
          </div>

          <button
            type="button"
            aria-label="Close help"
            className="rounded-full px-3 py-1 text-xl leading-none text-stone-500 hover:bg-stone-100"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className="mt-5 space-y-4 text-sm text-stone-700">
          <section>
            <h3 className="font-bold text-stone-800">Tool guide</h3>

            <div className="mt-2 grid gap-2">
              {HELP_TEXT.map((item) => (
                <HelpItem
                  icon={item.icon}
                  title={item.title}
                  text={item.text}
                />
              ))}
            </div>
          </section>

          <section>
            <h3 className="font-bold text-stone-800 mb-2">
              Make your post stand out
            </h3>
            <ul className="list-disc pl-4">
              <li>Use a clear, bright photo of the animal.</li>
              <li>Keep the animal as the main focus.</li>
              <li>Make sure text is large enough to read on mobile.</li>
              <li>Don't add too much text — keep it simple.</li>
            </ul>
            <p className="italic mt-4">
              ** Please note: Only one photo can be added to a post.
            </p>
          </section>

          <section>
            <h3 className="font-bold text-stone-800 mb-2">Protect privacy</h3>
            <p className="mb-2">You can use stickers to cover:</p>
            <ul className="list-disc pl-4">
              <li>Faces of people in the background.</li>
              <li>Car license plates.</li>
              <li>Addresses or private details</li>
            </ul>
          </section>

          <section className="rounded-xl bg-lt-beige p-3">
            <h3 className="font-bold text-stone-800">💡 Helpful tip</h3>
            <p className="mt-1 italic">
              You'll add full details in the next step — use this image to catch
              attention.
            </p>
          </section>
          <section>
            <p className="mt-1">
              For help with something not covered here, please email{' '}
              <a
                className="underline text-button"
                href="mailto:help@rescueme.org"
              >
                help@rescueme.org
              </a>
            </p>
          </section>
        </div>

        <button
          type="button"
          className="mt-5 w-full rounded-xl bg-button px-4 py-2 font-bold text-white hover:bg-lt-brown transition duration-300"
          onClick={onClose}
        >
          Got it
        </button>
      </div>
    </div>
  );
}

function HelpItem({ icon, title, text }) {
  return (
    <div className="flex gap-3 rounded-xl border border-stone-200 p-3">
      <div className="text-lg">{icon}</div>
      <div>
        <div className="font-bold text-stone-800">{title}</div>
        <div className="text-stone-600">{text}</div>
      </div>
    </div>
  );
}
